import type { User } from '@ctcells/contract'
import type { UserRow } from '@ctcells/fixtures'

import { dateTimeText } from '../format.ts'

/**
 * `User`(계약) → `UserRow`(화면) 변환.
 *
 * 🔴 **화면이 계산하지 않는 표시값만 만든다.** 버튼 활성에 쓰이는 값은 한 글자도 만들지 않는다.
 *
 * ⚠ **실물이 주지 않아 비는 열 둘**(2026-09-17 실측 · 계약에도 없다).
 *
 * | 열              | 왜 비는가                                                                    |
 * | --------------- | ---------------------------------------------------------------------------- |
 * | `email`         | 🔴 `User` 에 **이메일 필드가 없다.** 계약·실물 양쪽에 0건 → `—`               |
 * | `last_seen_text`| 「최근 활동」인데 실물이 주는 것은 `last_login_at`(**마지막 로그인**)뿐이다   |
 *
 * → 지어내지 않고 대시로 흘린다. 필요한 API 는 보고 목록에 올린다.
 *
 * 🔴 **`user_id` 는 표시값이 아니지만 반드시 싣는다.** 상세 화면이 세션·감사를 거르는 축이다.
 *    예전에는 화면이 fixture(`USERS`)를 `username` 으로 뒤져 이 값을 얻었고, 실물에는 그 fixture 가
 *    없어서 **세션 표·감사 표가 통째로 비었다.** 조회 축은 화면이 찾는 것이 아니라 행이 나른다.
 */
export function toUserRow(user: User): UserRow {
  return {
    // 🔴 표시용이 아니라 **조회 축**이다. 상세 화면이 세션·감사를 이 값으로 거른다
    user_id: user.user_id,
    username: user.username,
    // 🔴 예전에 여기가 `email` 이었고 계약에 그 필드가 없어 «항상» `—` 였다. `name` 은 실재한다
    name: user.name,
    role: user.role,
    status: user.status,
    // ⚠ 로그인 시각이지 활동 시각이 아니다. 열 이름과 값의 뜻이 어긋난다(보고 대상)
    last_seen_text: dateTimeText(user.last_login_at),
    // 원값 그대로. 상세 화면이 「마지막 로그인」·「잠금」에서 다시 포맷한다
    last_login_at: user.last_login_at ?? null,
    locked_until: user.locked_until ?? null,
  }
}

/**
 * 화면이 쓰는 행 목록 한 벌.
 *
 * 🔴 **세션을 받지 않는다**(2026-09-30). 예전에는 「세션」 열(사용자당 세션 수)을 세려고
 *    `GET /sessions` 를 같이 받았는데 그 열이 없어졌다(eric). 「마지막 로그인」은 `User.last_login_at`
 *    에서 온다 — 세션의 `last_seen_at` 이 아니다. 그래서 목록 화면은 `/sessions` 를 부를 까닭이 없다.
 */
export function toUserRows(users: readonly User[]): readonly UserRow[] {
  return users.map(toUserRow)
}
